import React, {PropsWithChildren, useCallback, useState} from 'react';

type AccordionModifiers = 'primary' | 'secondary' | 'tertiary';

interface AccordionItemProps {
    header: JSX.Element | string;
    open?: boolean;
    modifiers?: AccordionModifiers[];
}

function AccordionItem(props: PropsWithChildren<AccordionItemProps>): JSX.Element {
    const {header, open = false, modifiers = ['primary'], children} = props;

    const [isOpen, setIsOpen] = useState(open ?? false);
    const [isClosing, setIsClosing] = useState(false);
    const [isOpening, setIsOpening] = useState(false);

    const toggleDropdown = useCallback(
        (toggle: boolean) => {
            if (toggle === isOpen) return;

            if (toggle) {
                setIsOpening(true);

                setTimeout(() => {
                    setIsOpening(false);
                    setIsOpen(true);
                }, 300); // 300 is animation duration set in accordion.scss
            } else {
                setIsClosing(true);

                setTimeout(() => {
                    setIsClosing(false);
                    setIsOpen(false);
                }, 300); // 300 is animation duration set in accordion.scss
            }
        },
        [isOpen, setIsOpen]
    );

    return (
        <div
            className={`c-accordion__item ${isOpen ? 'is-open' : ''} ${isOpening ? 'is-opening' : ''} ${
                isClosing ? 'is-closing' : ''
            } ${getModifiers(modifiers)}`}
        >
            <div
                onClick={() => toggleDropdown(!isOpen)}
                onKeyUp={(event) => {
                    if (event.code === 'Enter') {
                        event.preventDefault();
                        toggleDropdown(!isOpen);
                    }
                }}
                className="c-accordion__title u-pointer"
                tabIndex={0}
                role="button"
                aria-expanded={isOpen}
            >
                {header}
            </div>
            <div className="c-accordion__body" role="region" aria-hidden={!isOpen}>
                <div>{children}</div>
            </div>
        </div>
    );
}

function getModifiers(modifiers?: AccordionModifiers[]): string {
    if (modifiers) {
        return modifiers.map((item) => 'c-accordion--'.concat(item)).join(' ');
    }
    return '';
}

export default AccordionItem;
